import { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Typography,
} from '@mui/material';
import { usePushNotificationContext } from '../../contexts/PushNotificationContext';

// ---------------------------------------------------------------------------
// Detecções de ambiente (imutáveis, calculadas uma vez)
// ---------------------------------------------------------------------------

const IS_IOS: boolean =
  /(iPad|iPhone|iPod)/.test(navigator.userAgent) ||
  (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);

const IS_STANDALONE: boolean =
  'standalone' in window.navigator && !!(window.navigator as Navigator & { standalone: boolean }).standalone;

const STORAGE_KEY = '@sconapp/ios-pwa-banner-dismissed';

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * Banner que orienta usuários a ativar notificações push no celular.
 *
 * Só aparece na página de Notificações (/notifications).
 *
 * Modos:
 *  1. iOS não-PWA       → instrui a adicionar à Tela de Início
 *  2. PWA + precisa de gesto → botão "Ativar notificações"
 *
 * "OK" descarta a sessão atual (localStorage — reaparece se usar outro
 * navegador ou se instalar o PWA).
 */
export const IosPwaBanner = () => {
  const { needsUserGesture, subscribeWithGesture } =
    usePushNotificationContext();

  // -------------------------------------------------------------------
  // Estado: banner "adicione à Tela de Início" (iOS não-PWA)
  // -------------------------------------------------------------------
  const [showPwaGuide, setShowPwaGuide] = useState(() => {
    if (!IS_IOS || IS_STANDALONE) return false;
    return !localStorage.getItem(STORAGE_KEY);
  });

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShowPwaGuide(false);
  };

  // -------------------------------------------------------------------
  // Botão "Ativar notificações" (modo gesture)
  // -------------------------------------------------------------------
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      await subscribeWithGesture();
    } finally {
      setSubscribing(false);
    }
  };

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  if (!showPwaGuide && !needsUserGesture) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {/* -------------------------------------------------------------- */}
      {/* Modo 1: iOS não-PWA — orientar a adicionar à Tela de Início  */}
      {/* -------------------------------------------------------------- */}
      {showPwaGuide && (
        <Collapse in={showPwaGuide}>
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={handleDismiss}>
                OK
              </Button>
            }
          >
            <AlertTitle>📱 Ative as notificações no iPhone/iPad</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Para receber notificações push, adicione o SconApp à Tela de
              Início:
            </Typography>
            <ol style={{ margin: 0, paddingLeft: '20px' }}>
              <li>
                Toque no botão <strong>Compartilhar</strong>{' '}
                <span role="img" aria-label="compartilhar">📤</span> no navegador
              </li>
              <li>
                Role e toque em <strong>Adicionar à Tela de Início</strong>
              </li>
              <li>
                Toque em <strong>Adicionar</strong> no canto superior direito
              </li>
            </ol>
          </Alert>
        </Collapse>
      )}

      {/* -------------------------------------------------------------- */}
      {/* Modo 2: PWA instalado, mas precisa de gesto para ativar push */}
      {/* -------------------------------------------------------------- */}
      {needsUserGesture && (
        <Alert
          severity="info"
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSubscribe}
                disabled={subscribing}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {subscribing ? 'Ativando…' : 'Ativar notificações'}
              </Button>
            </Box>
          }
        >
          <AlertTitle>🔔 Notificações push</AlertTitle>
          <Typography variant="body2">
            Toque em <strong>Ativar notificações</strong> para receber avisos de
            agendamentos, lembretes e muito mais.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};
